import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, storage, auth } from "../../../firebase.init";
import { setDoc, doc, updateDoc, deleteDoc, collection, query, where, getDocs, getDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import "./UploadDoc.css";
import { ToastContainer, toast } from "react-toastify";
import { faArrowLeft, faFolder, faClose, faSort, faEdit, faTrash, faPlus, faEye, faDownload } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuthState } from "react-firebase-hooks/auth";
import ClipLoader from "react-spinners/ClipLoader";
import { Modal as BootstrapModal, Button, Form, ListGroup, InputGroup, Spinner } from 'react-bootstrap';

const UploadDoc = ({ onUpload }) => {
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [types, setTypes] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [newFolderName, setNewFolderName] = useState("");
    const { uid } = useParams();
    const [actualUid, setActualUid] = useState(uid);
    const [loading, setLoading] = useState(false);
    const [user] = useAuthState(auth);
    const [buildingName, setBuildingName] = useState("");
    const navigate = useNavigate();
    const [viewedDocument, setViewedDocument] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [editingFolder, setEditingFolder] = useState(null);
    const [editedFolderName, setEditedFolderName] = useState("");
    const [sortOption, setSortOption] = useState("expiry");
    const [showFolderPickerModal, setShowFolderPickerModal] = useState(false);
    const [selectedContractorFolder, setSelectedContractorFolder] = useState(null);
    const fileInputRef = useRef();
    const [showPinModal, setShowPinModal] = useState(false);
    const [pin, setPin] = useState("");

    const [show, setShow] = useState(false);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [loadingEmails, setLoadingEmails] = useState(false);

    const [remLoading, setRemLoading] = useState(true);
    const [reminders, setReminders] = useState({ "24h": false, "7d": false, "30d": false });
    const [showReminderModal, setShowReminderModal] = useState(false);

    const fetchEmails = async () => {
        setEmails([]);
        setLoadingEmails(true);
        try {
            const folderRef = doc(db, `Reports/${uid}/Documents/${selectedFolder}`);
            const snap = await getDoc(folderRef);
            const list = snap.exists() ? snap.data().emails || [] : [];
            setEmails(list);
        } catch (err) {
            console.error("Failed to fetch emails:", err);
        } finally {
            setLoadingEmails(false);
        }
    };

    const handleAddEmail = async () => {
        setLoadingEmails(true);

        const email = newEmail.trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email || !emailRegex.test(email)) {
            alert("Please enter a valid email address.");
            return;
        }

        if (emails.map(e => e.toLowerCase()).includes(email)) {
            alert("This email is already added.");
            return;
        }

        try {
            const folderRef = doc(db, `Reports/${uid}/Documents/${selectedFolder}`);
            await setDoc(
                folderRef,
                { emails: arrayUnion(email) },
                { merge: true }
            );
            setEmails((prev) => [...prev, email]);
            setNewEmail("");
        } catch (err) {
            console.error("Error adding email:", err);
        } finally {
            setLoadingEmails(false);
        }
    };

    const handleDeleteEmail = async (email) => {
        try {
            const folderRef = doc(db, `Reports/${uid}/Documents/${selectedFolder}`);
            await updateDoc(folderRef, {
                emails: arrayRemove(email),
            });
            setEmails((prev) => prev.filter((e) => e !== email));
        } catch (err) {
            console.error("Error deleting email:", err);
        }
    };


    const handleShow = () => {
        setShow(true);
        fetchEmails();
    };

    const handleClose = () => setShow(false);

    // 
    useEffect(() => {
        if (show) {
            fetchReminderSettings();
        }
    }, [show]);

    const fetchReminderSettings = async () => {
        setReminders({
            "24h": false,
            "7d": false,
            "30d": false,
        });
        setRemLoading(true);
        try {
            const folderRef = doc(db, `Reports/${uid}/Documents/${selectedFolder}`);
            const snap = await getDoc(folderRef);
            const data = snap.exists() ? snap.data().reminders || {} : {};
            setReminders({
                "24h": data["24h"] || false,
                "7d": data["7d"] || false,
                "30d": data["30d"] || false,
            });
        } catch (err) {
            console.error("Failed to fetch reminder settings:", err);
        } finally {
            setRemLoading(false);
        }
    };

    const handleCheckboxChange = (key) => {
        setReminders((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleSave = async () => {
        setRemLoading(true);
        try {
            const folderRef = doc(db, `Reports/${uid}/Documents/${selectedFolder}`);
            await setDoc(folderRef, { reminders }, { merge: true });
        } catch (err) {
            console.error("Failed to save reminder settings:", err);
        } finally {
            setRemLoading(false);
            setShowReminderModal(false);
        }
    };
    // 

    const handleOpenModal = () => {
        setShowPinModal(true);
    };

    const handleVerifyPin = () => {
        if (pin === "2203") {
            setShowPinModal(false);
            setShowFolderPickerModal(true);
            setPin("");
        } else {
            alert("Incorrect PIN! Please try again.");
            setPin("");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const buildingRef = doc(db, `Reports/${uid}`);
                const buildingSnap = await getDoc(buildingRef);

                if (!buildingSnap.exists()) {
                    console.warn("No report found for UID");
                    setLoading(false);
                    return;
                }

                const currentBuildingName = buildingSnap.data().buildingName;
                setBuildingName(currentBuildingName);

                const reportsQuery = query(
                    collection(db, "Reports"),
                    where("buildingName", "==", currentBuildingName)
                );
                const reportsSnap = await getDocs(reportsQuery);

                const fetchedFiles = {};
                const allTypesSet = new Set();
                const defaultFolders = [
                    "Log Book",
                    "Building Drawings",
                    "Electrical Certificate",
                    "AOV Certificate",
                    "Dry Riser Certificate",
                ];

                const latestUid = uid;
                setActualUid(latestUid);

                const latestDocsRef = collection(db, `Reports/${latestUid}/Documents`);
                const latestDocsSnap = await getDocs(latestDocsRef);
                if (latestDocsSnap.empty) {
                    for (const folderName of defaultFolders) {
                        await setDoc(doc(latestDocsRef, folderName), { files: [] });
                    }
                    const typesRef = doc(db, `Reports/${latestUid}/Documents`, "Types");
                    const typesData = {};
                    defaultFolders.forEach(name => (typesData[name] = { timestamp: Date.now() }));
                    await setDoc(typesRef, typesData);
                }

                for (const reportDoc of reportsSnap.docs) {
                    const otherUid = reportDoc.id;
                    if (otherUid === latestUid) continue;

                    const documentsCollectionRef = collection(db, `Reports/${otherUid}/Documents`);
                    const documentSnapshots = await getDocs(documentsCollectionRef);

                    for (const docSnap of documentSnapshots.docs) {
                        const docId = docSnap.id;
                        if (docId === "Types") continue;

                        const files = docSnap.data().files || [];
                        allTypesSet.add(docId);

                        const latestFolderRef = doc(db, `Reports/${latestUid}/Documents`, docId);
                        const latestFolderSnap = await getDoc(latestFolderRef);

                        if (!latestFolderSnap.exists()) {
                            await setDoc(latestFolderRef, { files });
                        } else if (files.length > 0) {
                            const existingFiles = latestFolderSnap.data().files || [];
                            const mergedFiles = [...existingFiles];

                            for (const f of files) {
                                if (!existingFiles.find(e => e.path === f.path)) {
                                    mergedFiles.push(f);
                                }
                            }
                            await setDoc(latestFolderRef, { files: mergedFiles });
                        }

                        await deleteDoc(doc(db, `Reports/${otherUid}/Documents`, docId));
                    }

                    const typesRefOld = doc(db, `Reports/${otherUid}/Documents`, "Types");
                    const typesSnapOld = await getDoc(typesRefOld);
                    if (typesSnapOld.exists()) await deleteDoc(typesRefOld);
                }

                const latestDocsSnapAfter = await getDocs(collection(db, `Reports/${latestUid}/Documents`));
                for (const docSnap of latestDocsSnapAfter.docs) {
                    const docId = docSnap.id;
                    if (docId === "Types") continue;
                    const files = docSnap.data().files || [];
                    fetchedFiles[docId] = files;
                    allTypesSet.add(docId);
                }

                const sortedTypes = Array.from(allTypesSet).sort((a, b) => a.localeCompare(b));
                setTypes(sortedTypes);
                setUploadedFiles(fetchedFiles);

            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to fetch reports.");
            } finally {
                setLoading(false);
            }
        };


        fetchData();
    }, [uid]);


    const rebuildTypesFromDocuments = async (uid) => {
        const documentsCollectionRef = collection(db, `Reports/${uid}/Documents`);
        const documentSnapshots = await getDocs(documentsCollectionRef);

        const typesRef = doc(db, `Reports/${uid}/Documents`, "Types");
        const typesSnap = await getDoc(typesRef);
        const typesData = typesSnap.exists() ? typesSnap.data() : {};

        const rebuiltTypes = {};
        for (const docSnap of documentSnapshots.docs) {
            const docId = docSnap.id;
            if (docId === "Types") continue;

            rebuiltTypes[docId] = typesData[docId] || { timestamp: Date.now() };
        }

        await setDoc(typesRef, rebuiltTypes, { merge: true });

        setTypes(Object.keys(rebuiltTypes).sort((a, b) => a.localeCompare(b)));
    };

    const handleAddFolder = async () => {
        if (!newFolderName.trim()) return;

        await rebuildTypesFromDocuments(actualUid);

        const typesRef = doc(db, `Reports/${actualUid}/Documents`, "Types");
        const typesSnap = await getDoc(typesRef);
        let currentTypes = typesSnap.exists() ? typesSnap.data() : {};

        if (currentTypes[newFolderName]) {
            toast.warning("Folder already exists.");
            return;
        }

        currentTypes[newFolderName] = { timestamp: Date.now() };
        await setDoc(typesRef, currentTypes, { merge: true });

        const folderDocRef = doc(db, `Reports/${actualUid}/Documents`, newFolderName);
        await setDoc(folderDocRef, { files: [] });

        setTypes(Object.keys(currentTypes).sort((a, b) => a.localeCompare(b)));
        toast.success("New folder added!");
        setNewFolderName("");
    };


    const handleRenameFolder = async (oldName) => {
        if (!editedFolderName.trim() || editedFolderName === oldName) {
            setEditingFolder(editedFolderName);
            setTimeout(() => setEditingFolder(null), 0);
            return;
        }

        if (window.confirm(`Are you sure you want to rename "${oldName}" to "${editedFolderName}"?`)) {
            setLoading(true);
            try {

                const typesRef = doc(db, `Reports/${actualUid}/Documents`, "Types");
                const typesSnap = await getDoc(typesRef);
                if (typesSnap.exists()) {
                    const typesData = typesSnap.data();
                    if (typesData[editedFolderName]) {
                        toast.warning("A folder with this name already exists.");
                        return;
                    }

                    const timestamp = typesData[oldName]?.timestamp || Date.now();
                    delete typesData[oldName];
                    typesData[editedFolderName] = { timestamp };

                    await setDoc(typesRef, typesData);

                    const oldFolderRef = doc(db, `Reports/${actualUid}/Documents`, oldName);
                    const oldFolderSnap = await getDoc(oldFolderRef);

                    if (oldFolderSnap.exists()) {
                        const newFolderRef = doc(db, `Reports/${actualUid}/Documents`, editedFolderName);
                        await setDoc(newFolderRef, oldFolderSnap.data());

                        await deleteDoc(oldFolderRef);

                        setTypes(Object.keys(typesData).sort((a, b) => a.localeCompare(b)));
                        setUploadedFiles(prev => {
                            const newFiles = { ...prev };
                            newFiles[editedFolderName] = newFiles[oldName];
                            delete newFiles[oldName];
                            return newFiles;
                        });

                        if (selectedFolder === oldName) {
                            setSelectedFolder(editedFolderName);
                        }

                        toast.success("Folder renamed successfully!");
                    }
                }
            } catch (error) {
                console.error("Error renaming folder:", error);
                toast.error("Failed to rename folder.");
            } finally {
                setEditingFolder(null);
                setEditedFolderName("");
                setLoading(false);
            }
        }
    };

    const handleDeleteFolder = async (folderName) => {
        if (window.confirm(`Are you sure you want to delete the folder "${folderName}" and all its contents?`)) {
            setLoading(true);
            try {
                const typesRef = doc(db, `Reports/${actualUid}/Documents`, "Types");
                const typesSnap = await getDoc(typesRef);

                if (typesSnap.exists()) {
                    const typesData = typesSnap.data();
                    delete typesData[folderName];
                    await setDoc(typesRef, typesData);
                }

                const folderRef = doc(db, `Reports/${actualUid}/Documents`, folderName);
                const folderSnap = await getDoc(folderRef);

                if (folderSnap.exists()) {

                    const files = folderSnap.data().files || [];
                    for (const file of files) {
                        try {
                            const fileRef = ref(storage, file.path);
                            await deleteObject(fileRef);
                        } catch (error) {
                            console.error(`Error deleting file ${file.name}:`, error);
                        }
                    }

                    await deleteDoc(folderRef);

                    setTypes((prev) => {
                        const updated = prev.filter(name => name !== folderName);
                        return [...updated];
                    });
                    setUploadedFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[folderName];
                        return newFiles;
                    });

                    if (selectedFolder === folderName) {
                        setSelectedFolder(null);
                    }

                    toast.success("Folder and all contents deleted successfully!");
                }
            } catch (error) {
                console.error("Error deleting folder:", error);
                toast.error("Failed to delete folder.");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleFileChange = async (event, folder) => {
        const files = event.target.files;
        if (!files.length) return;
        setLoading(true);

        let expiryDate = null;
        const inputDate = prompt("Enter expiry date (DD/MM/YYYY) or leave blank for none");
        if (inputDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(inputDate)) {
            alert("Invalid expiry date format. Please enter in DD/MM/YYYY format.");
            setLoading(false);
            return;
        }
        if (inputDate) {
            const [day, month, year] = inputDate.split("/");
            expiryDate = `${year}-${month}-${day}`;
        }


        try {
            const folderDocRef = doc(db, `Reports/${actualUid}/Documents`, folder);
            const folderDocSnap = await getDoc(folderDocRef);
            let existingFiles = [];
            if (folderDocSnap.exists()) {
                existingFiles = folderDocSnap.data().files || [];
            }

            const newFileDataArr = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const storagePath = `Reports/${actualUid}/Documents/${folder}/${file.name}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);

                const now = new Date();

                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, "0");
                const day = String(now.getDate()).padStart(2, "0");
                const timestamp = `${year}-${month}-${day}`;

                const newFileData = {
                    name: file.name,
                    url: downloadURL,
                    expiryDate,
                    timestamp,
                    path: storagePath,
                };
                newFileDataArr.push(newFileData);
            }

            const updatedFiles = [...existingFiles, ...newFileDataArr];
            await setDoc(folderDocRef, { files: updatedFiles }, { merge: true });
            setUploadedFiles((prev) => ({ ...prev, [folder]: updatedFiles }));
            toast.success("Files uploaded successfully!");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Error uploading file(s).");
        } finally {
            setLoading(false);
        }
    };

    const uploadContractorFile = async (event, folder) => {
        const files = event.target.files;
        if (!files || !folder) return;

        let expiryDate = null;
        const inputDate = prompt("Enter expiry date (DD/MM/YYYY) or leave blank for none");
        if (inputDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(inputDate)) {
            alert("Invalid expiry date format. Please enter in DD/MM/YYYY format.");
            setLoading(false);
            return;
        }
        if (inputDate) {
            const [day, month, year] = inputDate.split("/");
            expiryDate = `${year}-${month}-${day}`;
        }

        setDisplayedText('Uploading your files...');
        setLoading(true);

        try {
            const folderDocRef = doc(db, `Reports/${actualUid}/Documents`, folder);
            const folderDocSnap = await getDoc(folderDocRef);
            let existingFiles = folderDocSnap.exists() ? folderDocSnap.data().files || [] : [];

            const newFileDataArr = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const storagePath = `Reports/${actualUid}/Documents/${folder}/${file.name}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);

                const now = new Date();
                const timestamp = now.toISOString().split("T")[0];

                newFileDataArr.push({
                    name: file.name,
                    url: downloadURL,
                    expiryDate,
                    timestamp,
                    path: storagePath,
                });
            }

            const updatedFiles = [...existingFiles, ...newFileDataArr];
            await setDoc(folderDocRef, { files: updatedFiles }, { merge: true });
            setUploadedFiles((prev) => ({ ...prev, [folder]: updatedFiles }));
            toast.success("Files uploaded successfully!");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Error uploading file(s).");
        } finally {
            setLoading(false);
        }
    };


    const handleDownload = async (file) => {
        toast.success("Download started!");
        setLoading(true);
        setDownloading(true);
        try {
            const response = await fetch(file.url);
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = file.name;
            link.click();
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Error downloading file.");
        } finally {
            setLoading(false);
            setDownloading(false);
        }
    };
    const handleDeleteFile = async (folder, fileIndex) => {
        if (window.confirm("Are you sure you want to delete this file?")) {
            if (!uploadedFiles[folder] || !uploadedFiles[folder][fileIndex]) return;
            setLoading(true);
            try {
                const file = uploadedFiles[folder][fileIndex];
                const fileRef = ref(storage, file.path);
                await deleteObject(fileRef);

                const updatedFiles = uploadedFiles[folder].filter((_, index) => index !== fileIndex);
                const folderDocRef = doc(db, `Reports/${actualUid}/Documents`, folder);
                await setDoc(folderDocRef, { files: updatedFiles }, { merge: true });

                setUploadedFiles((prev) => ({ ...prev, [folder]: updatedFiles }));
                toast.info("File deleted successfully!");
            } catch (err) {
                console.error("Delete error:", err);
                toast.error("Error deleting file.");
            } finally {
                setLoading(false);
            }
        }
    };

    const sortedFiles = (files) => {
        if (!files) return [];

        return [...files].sort((a, b) => {
            switch (sortOption) {
                case "name":
                    return a.name.localeCompare(b.name);

                case "date": {
                    const aDate = a.timestamp || "";
                    const bDate = b.timestamp || "";
                    return bDate.localeCompare(aDate);
                }

                case "expiry": {
                    const aExpiry = a.expiryDate || "";
                    const bExpiry = b.expiryDate || "";

                    if (!aExpiry && !bExpiry) return 0;
                    if (!aExpiry) return 1;
                    if (!bExpiry) return -1;

                    return aExpiry.localeCompare(bExpiry);
                }

                default:
                    return 0;
            }
        });
    };


    const handleViewDocument = (file) => {
        setViewedDocument(file.url);
    };

    const handleCloseView = () => {
        setViewedDocument(null);
    };

    const messages = [
        "Fetching fire safety documents...",
        "Compiling results...",
        "Almost there..."
    ];

    const [messageIndex, setMessageIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState('');

    useEffect(() => {
        const currentMessage = messages[messageIndex];

        if (charIndex < currentMessage.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(currentMessage.slice(0, charIndex + 1));
                setCharIndex((prev) => prev + 1);
            }, 30);
            return () => clearTimeout(timeout);
        } else if (messageIndex < messages.length - 1) {
            const delay = setTimeout(() => {
                setMessageIndex((prev) => prev + 1);
                setCharIndex(0);
                setDisplayedText('');
            }, 1500);
            return () => clearTimeout(delay);
        }
    }, [charIndex, messageIndex, messages]);

    if (loading) return (
        <div className="loading-container">
            <ClipLoader color="#b11111" size={60} />
            <div className="loading-text">
                {displayedText}
                <span className="cursor">|</span>
            </div>
        </div>
    );

    return (
        <div className="upload-container">
            {loading && (
                <div className="loading-container">
                    <div className="spinner"></div>
                </div>
            )}

            <div
                className="d-flex align-items-center"
                style={{ marginBottom: "20px", position: "relative" }}
            >
                <button
                    className="btn btn-link text-decoration-none d-flex align-items-center"
                    onClick={() => (selectedFolder ? setSelectedFolder(null) : navigate(-1))}
                    style={{
                        maxWidth: "fit-content",
                        fontSize: "1rem",
                        color: "red",
                        fontWeight: "bold",
                        cursor: "pointer",
                    }}
                >
                    {window.innerWidth <= 768 ? (
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            style={{ color: "red", fontWeight: "bold", marginTop: "10px" }}
                        />
                    ) : (
                        <>
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                style={{ color: "red", fontWeight: "bold", marginRight: "0.5rem" }}
                            />
                            {selectedFolder ? "Back" : "Back"}
                        </>
                    )}
                </button>

                <h2 className="mb-0 ms-3" style={{ flexGrow: 1, textAlign: "center" }}>
                    {buildingName && `${buildingName}`}
                </h2>
            </div>

            {!selectedFolder && (
                <div>
                    <div className="add-folder d-flex justify-content-center align-items-center"
                        style={{ marginTop: "20px", marginBottom: "20px", textAlign: "center" }}>
                        {user ? (
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder="New folder name"
                                className="form-control"
                                style={{
                                    width: "400px",
                                    fontSize: window.innerWidth <= 768 ? "0.8rem" : "1rem",
                                    height: "40px",
                                    marginRight: "10px",
                                    borderRadius: '5px',
                                    border: '1px solid #ddd',
                                    borderColor: '#ff0000',
                                    boxShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
                                    transition: 'box-shadow 0.3s ease',
                                }}
                            />
                        ) :
                            (null)
                        }
                        {user ? (
                            <button
                                onClick={handleAddFolder}
                                className="btn btn-danger d-flex align-items-center justify-content-center"
                                style={{
                                    fontSize: window.innerWidth <= 768 ? "0.7rem" : "1rem",
                                    height: "40px",
                                    maxWidth: "140px",
                                    gap: "0.25rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon icon={faFolder} size="1x" color="#ffffff" />
                                Create
                            </button>

                        ) : (
                            <button
                                onClick={() => handleOpenModal(true)}
                                className="btn btn-danger"
                                style={{
                                    fontSize: window.innerWidth <= 768 ? "0.8rem" : "0.9rem",
                                    height: "40px",
                                    width: "160px",
                                    marginLeft: window.innerWidth <= 768 ? "60px" : "100px",
                                }}
                            >
                                Contractor Upload
                            </button>
                        )}

                    </div>

                    <h3>Folders</h3>
                    <div className="folder-list">
                        {types.map((folder, index) => (
                            <div
                                key={index}
                                className="folder-card"
                                onClick={user ? () => !editingFolder && setSelectedFolder(folder) : undefined}
                                style={{
                                    cursor: !user || editingFolder ? "not-allowed" : "pointer",
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    padding: "1rem",
                                    marginBottom: "0.5rem",
                                    width: "100%",
                                    backgroundColor: "#fff",
                                    transition: "background-color 0.2s ease",
                                }}
                            >
                                {editingFolder === folder ? (
                                    <div>
                                        <input
                                            type="text"
                                            value={editedFolderName}
                                            onChange={(e) => setEditedFolderName(e.target.value)}
                                            className="form-control mb-2"
                                            style={{
                                                fontSize: window.innerWidth <= 768 ? "0.7rem" : "1rem",
                                            }}
                                        />
                                        <div className="d-flex flex-wrap justify-content-center gap-1">
                                            <button
                                                className="btn btn-success btn-sm flex-fill"
                                                style={{ minWidth: "48%" }}
                                                onClick={() => handleRenameFolder(folder)}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm flex-fill"
                                                style={{ minWidth: "48%" }}
                                                onClick={() => setEditingFolder(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faFolder} size="3x" color="#ff0000" />
                                        <h5
                                            style={{
                                                marginTop: "0.5rem",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                fontSize: window.innerWidth <= 768 ? "0.7rem" : "1.1rem",
                                            }}
                                            title={folder}
                                        >
                                            {folder}
                                        </h5>
                                        <p style={{
                                            fontSize: window.innerWidth <= 768 ? "0.7rem" : "1.1rem",
                                        }}
                                        >
                                            {uploadedFiles[folder] ? uploadedFiles[folder].length : 0} files</p>
                                        <div className="folder-actions">
                                            {user && (
                                                <div className="row g-1 justify-content-center">
                                                    <div className="col-6 col-sm-auto d-flex justify-content-center">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary w-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingFolder(folder);
                                                                setEditedFolderName(folder);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                    </div>
                                                    <div className="col-6 col-sm-auto d-flex justify-content-center">
                                                        <button
                                                            className="btn btn-sm btn-outline-danger w-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteFolder(folder);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )
            }

            {
                selectedFolder && (
                    <div>
                        {/* Desktop - Original Layout (hidden on mobile) */}
                        <div className="d-none d-lg-block">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3>{selectedFolder} Files</h3>
                                <div className="d-flex align-items-center">
                                    <div className="mb-2 d-flex gap-2">
                                        <button
                                            onClick={() => setSortOption("name")}
                                            className={`btn btn-sm ${sortOption === "name" ? "btn-primary" : "btn-outline-primary"}`}
                                        >
                                            Sort by Name
                                        </button>
                                        <button
                                            onClick={() => setSortOption("date")}
                                            className={`btn btn-sm ${sortOption === "date" ? "btn-primary" : "btn-outline-primary"}`}
                                        >
                                            Sort by Date
                                        </button>
                                        <button
                                            onClick={() => setSortOption("expiry")}
                                            className={`btn btn-sm ${sortOption === "expiry" ? "btn-primary" : "btn-outline-primary"}`}
                                        >
                                            Sort by Expiry
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="file-upload-section mb-3">
                                <label className="btn btn-primary me-2">
                                    Upload Files
                                    <input
                                        type="file"
                                        accept="*"
                                        multiple
                                        onChange={(e) => handleFileChange(e, selectedFolder)}
                                        hidden
                                    />
                                </label>

                                <Button variant="success" className="me-2" onClick={handleShow}>
                                    Manage Recipients
                                </Button>
                            </div>
                        </div>

                        <div className="d-lg-none">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 text-truncate fw-bold">{selectedFolder}</h5>
                                <div className="dropdown">
                                    {/* <button
                                        className="btn btn-outline-secondary btn-sm dropdown-toggle d-flex align-items-center"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        <i className="bi bi-funnel me-1"></i>
                                        Sort
                                    </button> */}
                                    <ul className="dropdown-menu dropdown-menu-end shadow">
                                        <li>
                                            <button
                                                className={`dropdown-item d-flex align-items-center ${sortOption === "name" ? "active" : ""}`}
                                                onClick={() => setSortOption("name")}
                                            >
                                                <i className="bi bi-sort-alpha-down me-2"></i>
                                                Name
                                            </button>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <button
                                                className={`dropdown-item d-flex align-items-center ${sortOption === "date" ? "active" : ""}`}
                                                onClick={() => setSortOption("date")}
                                            >
                                                <i className="bi bi-calendar me-2"></i>
                                                Date
                                            </button>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <button
                                                className={`dropdown-item d-flex align-items-center ${sortOption === "expiry" ? "active" : ""}`}
                                                onClick={() => setSortOption("expiry")}
                                            >
                                                <i className="bi bi-clock me-2"></i>
                                                Expiry
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between mb-3">
                                <button
                                    onClick={() => setSortOption("name")}
                                    className={`btn btn-sm ${sortOption === "name" ? "btn-primary" : "btn-outline-secondary"} flex-fill mx-1`}
                                >
                                    Name
                                </button>
                                <button
                                    onClick={() => setSortOption("date")}
                                    className={`btn btn-sm ${sortOption === "date" ? "btn-primary" : "btn-outline-secondary"} flex-fill mx-1`}
                                >
                                    Date
                                </button>
                                <button
                                    onClick={() => setSortOption("expiry")}
                                    className={`btn btn-sm ${sortOption === "expiry" ? "btn-primary" : "btn-outline-secondary"} flex-fill mx-1`}
                                >
                                    Expiry
                                </button>
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <label className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center">
                                        <i style={{ fontSize: "0.7rem" }} className="bi bi-cloud-upload me-1"></i>
                                        <span className="text-nowrap">Upload</span>
                                        <input
                                            type="file"
                                            accept="*"
                                            multiple
                                            onChange={(e) => handleFileChange(e, selectedFolder)}
                                            className="d-none"
                                        />
                                    </label>
                                </div>
                                <div className="col-6">
                                    <Button
                                        variant="btn btn-success w-100 py-2 d-flex align-items-center justify-content-center"
                                        onClick={handleShow}
                                    >
                                        <i style={{ fontSize: "0.7rem" }} className="bi bi-person-plus me-1"></i>
                                        <span className="text-nowrap">Recipients</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* <Button variant="secondary" onClick={() => setShowReminderModal(true)}>
                                Reminder Settings
                            </Button> */}


                        <BootstrapModal show={show}>
                            <BootstrapModal.Header>
                                <BootstrapModal.Title>Email Recipients for Alerts</BootstrapModal.Title>
                            </BootstrapModal.Header>

                            <BootstrapModal.Body>
                                <h6>Current Recipients</h6>

                                {loadingEmails ? (
                                    <div className="d-flex justify-content-center my-3">
                                        <Spinner animation="border" />
                                    </div>
                                ) : emails.length === 0 ? (
                                    <p className="text-muted">No recipients added yet.</p>
                                ) : (
                                    <ListGroup className="mb-3">
                                        {emails.map((email) => (
                                            <ListGroup.Item
                                                key={email}
                                                className="d-flex justify-content-between align-items-center"
                                            >
                                                {email}
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="text-danger"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleDeleteEmail(email)}
                                                />
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}

                                <Form.Group className="mb-2">
                                    <Form.Label>Add New Email</Form.Label>
                                    <InputGroup style={{ flexWrap: 'nowrap', minWidth: 'min-content' }}>
                                        <Form.Control
                                            type="email"
                                            placeholder="Enter email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            style={{ minWidth: '210px' }}
                                        />
                                        <Button variant="success" onClick={handleAddEmail} style={{ minWidth: '0px' }}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </Button>
                                    </InputGroup>
                                </Form.Group>

                                {remLoading ? (
                                    <div className="d-flex justify-content-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : (
                                    <>
                                        <h6 className="mt-4">Reminder Settings</h6>
                                        <Form>
                                            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                                                {["24h", "7d", "30d"].map((key) => (
                                                    <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={reminders[key]}
                                                            onChange={() => handleCheckboxChange(key)}
                                                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                                        />
                                                        <label style={{ marginLeft: "8px", marginBottom: -10 }}>
                                                            {key === "24h" ? "24 hours" : key === "7d" ? "7 days" : "1 month"} before expiry
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </Form>
                                    </>
                                )}

                            </BootstrapModal.Body>


                            <BootstrapModal.Footer>
                                <Button variant="secondary" onClick={handleClose}>
                                    Close
                                </Button>
                            </BootstrapModal.Footer>
                        </BootstrapModal>

                        {/* <BootstrapModal show={showReminderModal} >
                            <BootstrapModal.Header>
                                <BootstrapModal.Title>Reminder Settings</BootstrapModal.Title>
                            </BootstrapModal.Header>

                            <BootstrapModal.Body>
                                {remLoading ? (
                                    <div className="d-flex justify-content-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : (
                                    <Form>
                                        <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                                            {["24h", "7d", "30d"].map((key, idx) => (
                                                <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={reminders[key]}
                                                        onChange={() => handleCheckboxChange(key)}
                                                        style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                                    />
                                                    <label style={{ marginLeft: "8px", marginBottom: -10 }}>
                                                        {key === "24h" ? "24 hours" : key === "7d" ? "7 days" : "1 month"} before expiry
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </Form>
                                )}
                            </BootstrapModal.Body>




                            <BootstrapModal.Footer>
                                <Button variant="secondary" onClick={() => setShowReminderModal(false)}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={handleSave}>
                                    Save Changes
                                </Button>
                            </BootstrapModal.Footer>
                        </BootstrapModal> */}

                        <div className="file-list">
                            {uploadedFiles[selectedFolder] && uploadedFiles[selectedFolder].length > 0 ? (
                                sortedFiles(uploadedFiles[selectedFolder]).map((file, index) => {
                                    const isExpired = file.expiryDate && new Date(file.expiryDate) < new Date();
                                    const displayExpiryDate = file.expiryDate
                                        ? file.expiryDate.split("-").reverse().join("/")
                                        : "";

                                    return (
                                        <div
                                            key={index}
                                            className="file-item d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2"
                                            style={{
                                                border: "1px solid #ddd",
                                                borderRadius: "8px",
                                                padding: "0.75rem",
                                                width: "100%",
                                            }}
                                        >
                                            <div className="file-info text-break" style={{ flex: 1, minWidth: "200px" }}>
                                                <strong style={{ wordBreak: "break-word" }}>{file.name}</strong>
                                                {file.expiryDate && (
                                                    <div
                                                        style={{
                                                            color: isExpired ? "red" : "black",
                                                            fontSize: "0.9rem",
                                                            marginTop: "0px",
                                                        }}
                                                    >
                                                        {isExpired ? "Expired: " : "Expiry: "} {displayExpiryDate}
                                                    </div>
                                                )}
                                            </div>

                                            <div
                                                className="file-actions d-flex justify-content-center justify-content-md-end mt-2 mt-md-0"
                                                style={{ gap: "5px" }}
                                            >
                                                <button
                                                    className="btn btn-success btn-sm d-flex align-items-center justify-content-center flex-md-auto"
                                                    onClick={() => handleViewDocument(file)}
                                                    style={{ minWidth: "0", fontSize: "0.9rem" }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="d-md-none" />
                                                    <span className="d-none d-md-inline ms-1">View</span>
                                                </button>

                                                <button
                                                    className="btn btn-primary btn-sm d-flex align-items-center justify-content-center flex-md-auto"
                                                    onClick={() => handleDownload(file)}
                                                    disabled={downloading}
                                                    style={{ minWidth: "0", fontSize: "0.9rem" }}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} className="d-md-none" />
                                                    <span className="d-none d-md-inline ms-1">{downloading ? "Downloading..." : "Download"}</span>
                                                </button>

                                                <button
                                                    className="btn btn-danger btn-sm d-flex align-items-center justify-content-center flex-md-auto"
                                                    onClick={() => handleDeleteFile(selectedFolder, index)}
                                                    style={{ minWidth: "0", fontSize: "0.9rem" }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="d-md-none" />
                                                    <span className="d-none d-md-inline ms-1">Delete</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-center">No files uploaded yet.</p>
                            )}
                        </div>

                    </div>
                )
            }

            {
                viewedDocument && (
                    <div
                        className="modal show d-flex align-items-center justify-content-center"
                        style={{
                            display: "block",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                        }}
                    >
                        <div className="modal-dialog modal-xl" style={{ maxWidth: "95vw" }}>
                            <div className="modal-content" style={{ borderRadius: "10px", overflow: "hidden" }}>
                                <div
                                    className="modal-header"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "10px 15px",
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "1px solid #ddd",
                                        position: "relative",
                                    }}
                                >
                                    <h5 className="modal-title">Document Viewer</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={handleCloseView}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            fontSize: "2.0rem",
                                            cursor: "pointer",
                                            position: "absolute",
                                            top: "50%",
                                            right: "15px",
                                            transform: "translateY(-50%)",
                                            color: "red",
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faClose} />
                                    </button>
                                </div>
                                <div
                                    className="modal-body d-flex justify-content-center align-items-center"
                                    style={{ padding: 0 }}
                                >
                                    <iframe
                                        src={viewedDocument}
                                        style={{
                                            width: window.innerWidth <= 768 ? "90vw" : "45vw",
                                            height: window.innerWidth <= 768 ? "70vh" : "80vh",
                                            border: "none",
                                            display: "block",
                                        }}
                                        title="Document Viewer"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            <ToastContainer />

            <BootstrapModal
                show={showFolderPickerModal}
                onHide={() => setShowFolderPickerModal(false)}
                centered
                backdrop="static"
                keyboard={false}
            >
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title>Select Folder</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <div
                        className="folder-list"
                        // style={{
                        //     display: "flex",
                        //     flexDirection: "column",
                        //     gap: "0.75rem",
                        // }}
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            maxHeight: "300px",
                            overflowY: "auto",
                            // paddingRight: "4px",
                            gap: "1rem",
                        }}
                    >
                        {types.map((folder, index) => (
                            <div
                                key={index}
                                className="folder-list-item"
                                style={{
                                    flex: "0 0 47%",
                                    display: "flex",
                                    alignItems: "center",
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    padding: "0.75rem 1rem",
                                    cursor: "pointer",
                                    backgroundColor: "#f9f9f9",
                                    transition: "background-color 0.2s ease",
                                }}
                                onClick={() => {
                                    setSelectedContractorFolder(folder);
                                    setShowFolderPickerModal(false);

                                    setTimeout(() => {
                                        const input = document.getElementById("contractor-file-input");
                                        if (input) {
                                            input.value = "";
                                            input.click();
                                        }
                                    }, 300);
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f1f1")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f9f9f9")}
                            >
                                <FontAwesomeIcon icon={faFolder} size="lg" style={{ marginRight: "12px", color: "#555" }} />
                                <span style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>{folder}</span>
                            </div>
                        ))}
                    </div>
                </BootstrapModal.Body>
            </BootstrapModal>


            <input
                type="file"
                id="contractor-file-input"
                hidden
                multiple
                accept="*"
                onChange={(e) => uploadContractorFile(e, selectedContractorFolder)}
            />

            <BootstrapModal show={showPinModal} onHide={() => setShowPinModal(false)} centered>
                <BootstrapModal.Header style={{ backgroundColor: "red", color: "white" }}>
                    <BootstrapModal.Title>Enter PIN</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <Form.Group controlId="pinInput">
                        <Form.Label>Enter your PIN:</Form.Label>
                        <Form.Control
                            type="password"
                            maxLength="4"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            placeholder="****"
                        />
                    </Form.Group>
                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setPin("");
                        setShowPinModal(false)
                    }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleVerifyPin}>
                        Submit
                    </Button>
                </BootstrapModal.Footer>
            </BootstrapModal>

        </div >
    );
};

export default UploadDoc;
